'use client'

import React from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { 
  Activity,
  User,
  Edit,
  Trash2,
  Plus,
  Download,
  Upload,
  Key,
  Shield,
  Settings,
  Eye,
  Search,
  Filter,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  Info,
  MapPin,
  Monitor,
  ChevronDown,
  FileText
} from 'lucide-react'
import type { 
  BirhausAuditTrailProps,
  AuditEvent,
  AuditEventType,
  AuditSeverity
} from '../types/compliance'

// Local cn utility
const cn = (...inputs: any[]) => twMerge(clsx(inputs))

// Audit event type configuration
const eventTypeConfig: Record<AuditEventType, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  acceso: {
    icon: <Eye size={16} />,
    colorClass: 'text-blue-600 bg-blue-100',
    nameEs: 'Acceso',
    nameEn: 'Access'
  },
  modificacion: {
    icon: <Edit size={16} />,
    colorClass: 'text-yellow-600 bg-yellow-100',
    nameEs: 'Modificación',
    nameEn: 'Modification'
  },
  eliminacion: {
    icon: <Trash2 size={16} />,
    colorClass: 'text-red-600 bg-red-100',
    nameEs: 'Eliminación',
    nameEn: 'Deletion'
  },
  creacion: {
    icon: <Plus size={16} />,
    colorClass: 'text-green-600 bg-green-100',
    nameEs: 'Creación',
    nameEn: 'Creation'
  },
  exportacion: {
    icon: <Download size={16} />,
    colorClass: 'text-purple-600 bg-purple-100',
    nameEs: 'Exportación',
    nameEn: 'Export'
  },
  importacion: {
    icon: <Upload size={16} />,
    colorClass: 'text-indigo-600 bg-indigo-100',
    nameEs: 'Importación',
    nameEn: 'Import'
  },
  autenticacion: {
    icon: <Key size={16} />,
    colorClass: 'text-orange-600 bg-orange-100',
    nameEs: 'Autenticación',
    nameEn: 'Authentication'
  },
  autorizacion: {
    icon: <Shield size={16} />,
    colorClass: 'text-cyan-600 bg-cyan-100',
    nameEs: 'Autorización',
    nameEn: 'Authorization'
  },
  configuracion: {
    icon: <Settings size={16} />,
    colorClass: 'text-gray-600 bg-gray-100',
    nameEs: 'Configuración',
    nameEn: 'Configuration'
  }
}

// Audit severity configuration
const severityConfig: Record<AuditSeverity, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  baja: {
    icon: <Info size={16} />,
    colorClass: 'text-green-700 bg-green-100',
    nameEs: 'Baja',
    nameEn: 'Low'
  },
  media: {
    icon: <Clock size={16} />,
    colorClass: 'text-yellow-700 bg-yellow-100',
    nameEs: 'Media',
    nameEn: 'Medium'
  },
  alta: {
    icon: <AlertTriangle size={16} />,
    colorClass: 'text-orange-700 bg-orange-100',
    nameEs: 'Alta',
    nameEn: 'High'
  },
  critica: {
    icon: <AlertTriangle size={16} />,
    colorClass: 'text-red-700 bg-red-100',
    nameEs: 'Crítica',
    nameEn: 'Critical'
  }
}

/**
 * BirhausAuditTrail - Comprehensive audit trail component
 * 
 * Features:
 * - Complete activity logging and monitoring for compliance
 * - Multi-level filtering by event type, severity, user, and time range
 * - Real-time search across all audit event properties
 * - Spanish-first event descriptions and categorization
 * - Risk indicator highlighting and alerting
 * - Detailed event context with before/after states
 * - User activity tracking with IP and device information
 * - Export functionality for compliance reporting
 * - Miller's Law compliance (max 7 visible events per view)
 * - Integrity verification with event hashing
 */
export function BirhausAuditTrail({
  events = [],
  filters = {},
  onEventClick,
  onExport,
  showExport = true,
  showFilters = true,
  pageSize = 7,
  className,
  ...props
}: BirhausAuditTrailProps) {
  const { language, reportViolation } = useBirhaus()
  
  // State
  const [searchTerm, setSearchTerm] = React.useState('')
  const [localFilters, setLocalFilters] = React.useState(filters)
  const [showFiltersPanel, setShowFiltersPanel] = React.useState(false)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [expandedEvents, setExpandedEvents] = React.useState<Set<string>>(new Set())

  // BIRHAUS validation: Check cognitive load
  React.useEffect(() => {
    if (pageSize > 7) {
      reportViolation({
        type: 'cognitive',
        severity: 'warning',
        element: document.createElement('div'),
        message: `Audit trail shows ${pageSize} events per page (max recommended: 7)`,
        messageEs: `Pista de auditoría muestra ${pageSize} eventos por página (máximo recomendado: 7)`,
        messageEn: `Audit trail shows ${pageSize} events per page (max recommended: 7)`,
        recommendation: 'Use pagination to maintain cognitive load limits',
        birhausPrinciple: 1 // Cognitive load reduction
      })
    }
  }, [pageSize, reportViolation])

  // Filter events
  const filteredEvents = React.useMemo(() => {
    let filtered = events

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(event =>
        event.descripcion.toLowerCase().includes(term) ||
        event.recurso.toLowerCase().includes(term) ||
        event.accion.toLowerCase().includes(term) ||
        event.usuario.nombre.toLowerCase().includes(term) ||
        event.usuario.email.toLowerCase().includes(term) ||
        event.contexto.modulo.toLowerCase().includes(term) ||
        event.contexto.funcionalidad.toLowerCase().includes(term)
      )
    }

    // Date range filters
    if (localFilters.dateFrom) {
      filtered = filtered.filter(event => new Date(event.timestamp) >= new Date(localFilters.dateFrom!))
    }
    if (localFilters.dateTo) {
      filtered = filtered.filter(event => new Date(event.timestamp) <= new Date(localFilters.dateTo!))
    }

    // Event type filter
    if (localFilters.eventTypes?.length) {
      filtered = filtered.filter(event => localFilters.eventTypes!.includes(event.tipo))
    }

    // Severity filter
    if (localFilters.severity?.length) {
      filtered = filtered.filter(event => localFilters.severity!.includes(event.severidad))
    }

    // User filter
    if (localFilters.user) {
      const userTerm = localFilters.user.toLowerCase()
      filtered = filtered.filter(event =>
        event.usuario.nombre.toLowerCase().includes(userTerm) ||
        event.usuario.email.toLowerCase().includes(userTerm)
      )
    }

    // Resource filter
    if (localFilters.resource) {
      const resourceTerm = localFilters.resource.toLowerCase()
      filtered = filtered.filter(event =>
        event.recurso.toLowerCase().includes(resourceTerm)
      )
    }

    return filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
  }, [events, searchTerm, localFilters])

  // Paginate results
  const totalPages = Math.ceil(filteredEvents.length / pageSize)
  const paginatedEvents = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredEvents.slice(start, start + pageSize)
  }, [filteredEvents, currentPage, pageSize])

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    const total = filteredEvents.length
    const critical = filteredEvents.filter(e => e.severidad === 'critica').length
    const high = filteredEvents.filter(e => e.severidad === 'alta').length
    const riskEvents = filteredEvents.filter(e => e.indicadorRiesgo).length
    const uniqueUsers = new Set(filteredEvents.map(e => e.usuario.id)).size
    
    // Recent activity (last 24 hours)
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
    const recentActivity = filteredEvents.filter(e => new Date(e.timestamp) >= yesterday).length

    return {
      total,
      critical,
      high,
      riskEvents,
      uniqueUsers,
      recentActivity
    }
  }, [filteredEvents])

  // Handle actions
  const handleEventClick = React.useCallback((event: AuditEvent) => {
    onEventClick?.(event)
  }, [onEventClick])

  const handleExport = React.useCallback(() => {
    onExport?.(filteredEvents)
  }, [onExport, filteredEvents])

  const toggleEventExpansion = React.useCallback((eventId: string) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev)
      if (newSet.has(eventId)) {
        newSet.delete(eventId)
      } else {
        newSet.add(eventId)
      }
      return newSet
    })
  }, [])

  const updateFilters = React.useCallback((updates: typeof filters) => {
    setLocalFilters(prev => ({ ...prev, ...updates }))
    setCurrentPage(1)
  }, [])

  const clearFilters = React.useCallback(() => {
    setLocalFilters({})
    setSearchTerm('')
    setCurrentPage(1)
  }, [])

  return (
    <div className={cn('space-y-6', className)} {...props}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-birhaus-foreground">
            {language === 'es' ? 'Pista de Auditoría' : 'Audit Trail'}
          </h1>
          <p className="text-birhaus-muted-foreground">
            {language === 'es' 
              ? 'Seguimiento completo de actividad del sistema'
              : 'Complete system activity tracking'
            }
          </p>
        </div>
        
        {showExport && (
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-birhaus-primary text-white rounded-lg hover:bg-birhaus-primary/90 transition-colors"
          >
            <Download size={16} />
            <span>{language === 'es' ? 'Exportar' : 'Export'}</span>
          </button>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-birhaus-foreground">{summaryStats.total}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Total' : 'Total'}
          </p>
        </div>
        
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-red-600">{summaryStats.critical}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Crítico' : 'Critical'}
          </p>
        </div>
        
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-orange-600">{summaryStats.high}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Alto' : 'High'}
          </p>
        </div>
        
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-yellow-600">{summaryStats.riskEvents}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Riesgo' : 'Risk'}
          </p>
        </div>
        
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-blue-600">{summaryStats.uniqueUsers}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Usuarios' : 'Users'}
          </p>
        </div>
        
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-green-600">{summaryStats.recentActivity}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? '24h' : '24h'}
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      {(showFilters || searchTerm) && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-birhaus-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={language === 'es' ? 'Buscar eventos...' : 'Search events...'}
                className="w-full pl-10 pr-4 py-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary focus:border-birhaus-primary"
              />
            </div>

            {/* Filter toggle */}
            {showFilters && (
              <button
                onClick={() => setShowFiltersPanel(!showFiltersPanel)}
                className="flex items-center gap-2 px-4 py-2 border border-birhaus-border rounded-lg hover:bg-birhaus-muted/50 transition-colors"
              >
                <Filter size={16} />
                <span>{language === 'es' ? 'Filtros' : 'Filters'}</span>
                <ChevronDown size={16} className={cn(
                  'transition-transform duration-200',
                  showFiltersPanel && 'rotate-180'
                )} />
              </button>
            )}
          </div>

          {/* Filters panel */}
          {showFilters && showFiltersPanel && (
            <div className="p-4 border border-birhaus-border rounded-lg bg-birhaus-muted/10 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Date from */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {language === 'es' ? 'Fecha Desde' : 'Date From'}
                  </label>
                  <input
                    type="datetime-local"
                    value={localFilters.dateFrom ? new Date(localFilters.dateFrom).toISOString().slice(0, 16) : ''}
                    onChange={(e) => updateFilters({ dateFrom: e.target.value ? new Date(e.target.value) : undefined })}
                    className="w-full p-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
                  />
                </div>

                {/* Date to */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {language === 'es' ? 'Fecha Hasta' : 'Date To'}
                  </label>
                  <input
                    type="datetime-local"
                    value={localFilters.dateTo ? new Date(localFilters.dateTo).toISOString().slice(0, 16) : ''}
                    onChange={(e) => updateFilters({ dateTo: e.target.value ? new Date(e.target.value) : undefined })}
                    className="w-full p-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
                  />
                </div>

                {/* Event type */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {language === 'es' ? 'Tipo de Evento' : 'Event Type'}
                  </label>
                  <select
                    multiple
                    value={localFilters.eventTypes || []}
                    onChange={(e) => updateFilters({ 
                      eventTypes: Array.from(e.target.selectedOptions, option => option.value as AuditEventType) 
                    })}
                    className="w-full p-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
                  >
                    {Object.entries(eventTypeConfig).map(([key, config]) => (
                      <option key={key} value={key}>
                        {language === 'es' ? config.nameEs : config.nameEn}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Severity */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {language === 'es' ? 'Severidad' : 'Severity'}
                  </label>
                  <select
                    multiple
                    value={localFilters.severity || []}
                    onChange={(e) => updateFilters({ 
                      severity: Array.from(e.target.selectedOptions, option => option.value as AuditSeverity) 
                    })}
                    className="w-full p-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
                  >
                    {Object.entries(severityConfig).map(([key, config]) => (
                      <option key={key} value={key}>
                        {language === 'es' ? config.nameEs : config.nameEn}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Clear filters */}
              <div className="flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-birhaus-muted-foreground hover:text-birhaus-foreground transition-colors"
                >
                  {language === 'es' ? 'Limpiar Filtros' : 'Clear Filters'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Events List */}
      <div className="space-y-4">
        {paginatedEvents.length === 0 ? (
          <div className="text-center py-12 bg-white border border-birhaus-border rounded-lg">
            <Activity size={48} className="mx-auto mb-4 text-birhaus-muted-foreground/50" />
            <h3 className="text-lg font-semibold text-birhaus-foreground mb-2">
              {language === 'es' ? 'No hay eventos de auditoría' : 'No audit events found'}
            </h3>
            <p className="text-birhaus-muted-foreground">
              {searchTerm || Object.keys(localFilters).length > 0 ? (
                language === 'es' 
                  ? 'No se encontraron eventos con los filtros aplicados'
                  : 'No events found with applied filters'
              ) : (
                language === 'es'
                  ? 'Los eventos de auditoría aparecerán aquí cuando ocurran actividades'
                  : 'Audit events will appear here when activities occur'
              )}
            </p>
          </div>
        ) : (
          paginatedEvents.map((event) => {
            const typeConfig = eventTypeConfig[event.tipo]
            const severityInfo = severityConfig[event.severidad]
            const isExpanded = expandedEvents.has(event.id)
            const isClickable = Boolean(onEventClick)

            return (
              <div
                key={event.id}
                className={cn(
                  'bg-white border rounded-lg transition-all duration-200',
                  event.indicadorRiesgo ? 'border-yellow-300 bg-yellow-50' : 'border-birhaus-border',
                  event.severidad === 'critica' && 'border-red-300 bg-red-50',
                  isClickable && 'cursor-pointer hover:shadow-md hover:border-birhaus-primary/50'
                )}
                onClick={() => isClickable && handleEventClick(event)}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={cn('p-2 rounded-full flex-shrink-0', typeConfig.colorClass)}>
                        {typeConfig.icon}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-birhaus-foreground">
                            {event.descripcion}
                          </h3>
                          <span className={cn(
                            'flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full',
                            severityInfo.colorClass
                          )}>
                            {severityInfo.icon}
                            <span>{language === 'es' ? severityInfo.nameEs : severityInfo.nameEn}</span>
                          </span>
                          
                          {event.indicadorRiesgo && (
                            <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full text-yellow-700 bg-yellow-100">
                              <AlertTriangle size={12} />
                              <span>{language === 'es' ? 'Riesgo' : 'Risk'}</span>
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-birhaus-muted-foreground mb-1">
                              {language === 'es' ? 'Usuario' : 'User'}
                            </p>
                            <p className="font-medium flex items-center gap-1">
                              <User size={12} />
                              {event.usuario.nombre}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-birhaus-muted-foreground mb-1">
                              {language === 'es' ? 'Recurso' : 'Resource'}
                            </p>
                            <p className="font-medium">
                              {event.recurso}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-birhaus-muted-foreground mb-1">
                              {language === 'es' ? 'Acción' : 'Action'}
                            </p>
                            <p className="font-medium">
                              {event.accion}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-birhaus-muted-foreground mb-1">
                              {language === 'es' ? 'Fecha/Hora' : 'Date/Time'}
                            </p>
                            <p className="font-medium flex items-center gap-1">
                              <Clock size={12} />
                              {event.timestamp.toLocaleString(language === 'es' ? 'es-PY' : 'en-US')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Expand button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleEventExpansion(event.id)
                      }}
                      className="p-2 text-birhaus-muted-foreground hover:text-birhaus-foreground hover:bg-birhaus-muted rounded transition-colors"
                      aria-label={isExpanded ? (language === 'es' ? 'Contraer' : 'Collapse') : (language === 'es' ? 'Expandir' : 'Expand')}
                    >
                      <ChevronDown size={16} className={cn(
                        'transition-transform duration-200',
                        isExpanded && 'rotate-180'
                      )} />
                    </button>
                  </div>
                  
                  {/* Additional info */}
                  <div className="flex items-center gap-4 text-xs text-birhaus-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <MapPin size={12} />
                      {event.usuario.ip || 'N/A'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Monitor size={12} />
                      {event.contexto.modulo}
                    </span>
                    <span>
                      {language === 'es' ? 'Sesión:' : 'Session:'} {event.contexto.sesionId.slice(0, 8)}...
                    </span>
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-birhaus-border bg-birhaus-muted/10">
                    <div className="pt-4 space-y-4">
                      {/* User details */}
                      <div>
                        <h4 className="font-medium text-birhaus-foreground mb-2">
                          {language === 'es' ? 'Detalles del Usuario' : 'User Details'}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-birhaus-muted-foreground">Email:</span> {event.usuario.email}
                          </div>
                          <div>
                            <span className="text-birhaus-muted-foreground">
                              {language === 'es' ? 'Rol:' : 'Role:'}
                            </span> {event.usuario.rol}
                          </div>
                          {event.usuario.userAgent && (
                            <div className="sm:col-span-2">
                              <span className="text-birhaus-muted-foreground">User Agent:</span> {event.usuario.userAgent}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Context */}
                      <div>
                        <h4 className="font-medium text-birhaus-foreground mb-2">
                          {language === 'es' ? 'Contexto' : 'Context'}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-birhaus-muted-foreground">
                              {language === 'es' ? 'Módulo:' : 'Module:'}
                            </span> {event.contexto.modulo}
                          </div>
                          <div>
                            <span className="text-birhaus-muted-foreground">
                              {language === 'es' ? 'Funcionalidad:' : 'Function:'}
                            </span> {event.contexto.funcionalidad}
                          </div>
                          {event.contexto.requestId && (
                            <div className="sm:col-span-2">
                              <span className="text-birhaus-muted-foreground">Request ID:</span> {event.contexto.requestId}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Risk assessment */}
                      {event.indicadorRiesgo && (
                        <div>
                          <h4 className="font-medium text-birhaus-foreground mb-2">
                            {language === 'es' ? 'Evaluación de Riesgo' : 'Risk Assessment'}
                          </h4>
                          <div className="text-sm">
                            <p>
                              <span className="text-birhaus-muted-foreground">
                                {language === 'es' ? 'Puntuación de Riesgo:' : 'Risk Score:'}
                              </span> {event.puntuacionRiesgo || 'N/A'}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* State changes */}
                      {(event.estadoAnterior || event.estadoNuevo) && (
                        <div>
                          <h4 className="font-medium text-birhaus-foreground mb-2">
                            {language === 'es' ? 'Cambios de Estado' : 'State Changes'}
                          </h4>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {event.estadoAnterior && (
                              <div>
                                <p className="text-sm font-medium text-birhaus-muted-foreground mb-2">
                                  {language === 'es' ? 'Estado Anterior:' : 'Previous State:'}
                                </p>
                                <pre className="text-xs bg-red-50 p-3 rounded border overflow-x-auto">
                                  {JSON.stringify(event.estadoAnterior, null, 2)}
                                </pre>
                              </div>
                            )}
                            {event.estadoNuevo && (
                              <div>
                                <p className="text-sm font-medium text-birhaus-muted-foreground mb-2">
                                  {language === 'es' ? 'Estado Nuevo:' : 'New State:'}
                                </p>
                                <pre className="text-xs bg-green-50 p-3 rounded border overflow-x-auto">
                                  {JSON.stringify(event.estadoNuevo, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Metadata */}
                      {event.metadatos && Object.keys(event.metadatos).length > 0 && (
                        <div>
                          <h4 className="font-medium text-birhaus-foreground mb-2">
                            {language === 'es' ? 'Metadatos' : 'Metadata'}
                          </h4>
                          <pre className="text-xs bg-birhaus-muted/20 p-3 rounded border overflow-x-auto">
                            {JSON.stringify(event.metadatos, null, 2)}
                          </pre>
                        </div>
                      )}

                      {/* Integrity hash */}
                      <div>
                        <h4 className="font-medium text-birhaus-foreground mb-2">
                          {language === 'es' ? 'Integridad' : 'Integrity'}
                        </h4>
                        <div className="text-xs text-birhaus-muted-foreground font-mono bg-gray-100 p-2 rounded">
                          {event.hash}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm border border-birhaus-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-birhaus-muted/50 transition-colors"
          >
            {language === 'es' ? 'Anterior' : 'Previous'}
          </button>

          <span className="text-sm text-birhaus-muted-foreground">
            {language === 'es' 
              ? `Página ${currentPage} de ${totalPages} (${filteredEvents.length} eventos)`
              : `Page ${currentPage} of ${totalPages} (${filteredEvents.length} events)`
            }
          </span>

          <button
            onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm border border-birhaus-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-birhaus-muted/50 transition-colors"
          >
            {language === 'es' ? 'Siguiente' : 'Next'}
          </button>
        </div>
      )}
    </div>
  )
}

export type { BirhausAuditTrailProps }